/** Non-humanoid mob models: creeper, spider, pig, cow, sheep (+wool), chicken. Vanilla parts verbatim. */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import type { MobView } from '../../game/types';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat, type PartDef } from './core';

const PI = Math.PI;

export interface QuadPose {
  limbSwing: number; limbAmt: number;
  headY: number; headX: number;
  age: number;
  swell: number;
  airborne: boolean;
  sheared: boolean;
  color: number;
  /** Sheep grazing ticks left (40 -> 0), partial-tick adjusted. */
  eat?: number;
}

export interface Model {
  /** Everything below modelRoot (pixel space). */
  readonly parts: THREE.Group;
  readonly mats: EntityMat[];
  /** materials that take the creeper-style white flash / hurt (all of them) */
  animate(p: QuadPose): void;
  setView?(v: MobView): void;
}

const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false): CubeDef =>
  ({ tex, from, size, inflate, mirror });

abstract class Base implements Model {
  readonly parts = new THREE.Group();
  readonly mats: EntityMat[] = [];
  protected m: EntityMat;
  constructor(protected env: EnvUniforms, sheet: string, protected tw = 64, protected th = 32) {
    this.m = this.mat(sheet);
  }
  protected mat(sheet: string, tint?: number, emissive = false): EntityMat {
    const m = entityMaterial(this.env, sheetTexture(sheet), { tint, emissive });
    this.mats.push(m);
    return m;
  }
  protected add(def: PartDef, mat = this.m, parent: THREE.Object3D = this.parts): THREE.Group {
    const g = buildPart(def, this.tw, this.th, mat);
    parent.add(g);
    return g;
  }
  abstract animate(p: QuadPose): void;
  /** Per-frame view state (saddle layer, ...). */
  setView?(v: MobView): void;
}

/** Four-legged pose shared by pig / cow / sheep: legs rh, lh, rf, lf. */
function quadLegs(legs: THREE.Group[], p: QuadPose): void {
  const s = p.limbSwing * 0.6662, a = p.limbAmt * 1.4;
  legs[0].rotation.x = Math.cos(s) * a;
  legs[1].rotation.x = Math.cos(s + PI) * a;
  legs[2].rotation.x = Math.cos(s + PI) * a;
  legs[3].rotation.x = Math.cos(s) * a;
}

export class CreeperModel extends Base {
  private head: THREE.Group; private legs: THREE.Group[];
  constructor(env: EnvUniforms) {
    super(env, 'entity/creeper/creeper');
    this.head = this.add({ cubes: [cube([0, 0], [-4, -8, -4], [8, 8, 8])], pos: [0, 6, 0] });
    this.add({ cubes: [cube([16, 16], [-4, 0, -2], [8, 12, 4])], pos: [0, 6, 0] });
    const leg = (x: number, z: number): THREE.Group => this.add({ cubes: [cube([0, 16], [-2, 0, -2], [4, 6, 4])], pos: [x, 18, z] });
    this.legs = [leg(-2, 4), leg(2, 4), leg(-2, -4), leg(2, -4)];
  }
  animate(p: QuadPose): void {
    this.head.rotation.set(p.headX, p.headY, 0);
    const s = p.limbSwing * 0.6662, a = p.limbAmt * 1.4;
    this.legs[0].rotation.x = Math.cos(s) * a; this.legs[1].rotation.x = Math.cos(s + PI) * a;
    this.legs[2].rotation.x = Math.cos(s + PI) * a; this.legs[3].rotation.x = Math.cos(s) * a;
    // CreeperRenderer.scale: swell inflates the whole body
    let f = p.swell;
    const f1 = 1 + Math.sin(f * 100) * f * 0.01;
    f = Math.min(Math.max(f, 0), 1); f *= f; f *= f;
    const f2 = (1 + f * 0.4) * f1, f3 = (1 + f * 0.1) / f1;
    // scale about the feet: parts live in px with feet at y=24
    this.parts.scale.set(f2, f3, f2);
    this.parts.position.y = 24 * (1 - f3);
  }
}

export class SpiderModel extends Base {
  private head: THREE.Group; private legs: THREE.Group[] = [];
  constructor(env: EnvUniforms, sheet = 'entity/spider/spider') {
    super(env, sheet);
    this.head = this.add({ cubes: [cube([32, 4], [-4, -4, -8], [8, 8, 8])], pos: [0, 15, -3] });
    this.add({ cubes: [cube([0, 0], [-3, -3, -3], [6, 6, 6])], pos: [0, 15, 0] });
    this.add({ cubes: [cube([0, 12], [-5, -4, -6], [10, 8, 12])], pos: [0, 15, 9] });
    const eyes = this.mat('entity/spider/spider_eyes', undefined, true);
    this.add({ cubes: [cube([32, 4], [-4, -4, -8], [8, 8, 8], 0.01)] }, eyes, this.head);
    const z = [2, 2, 1, 1, 0, 0, -1, -1];
    for (let i = 0; i < 8; i++) {
      const right = i % 2 === 0;
      this.legs.push(this.add({
        cubes: [right ? cube([18, 0], [-15, -1, -1], [16, 2, 2]) : cube([18, 0], [-1, -1, -1], [16, 2, 2], 0, true)],
        pos: [right ? -4 : 4, 15, z[i]],
      }));
    }
    const f = PI / 4, f1 = PI / 8, k = 0.58119464;
    // [yRot, zRot] per leg 0..7 (right-hind, left-hind, ... right-front, left-front), vanilla SpiderModel
    const yz: Array<[number, number]> = [
      [f, -f], [-f, f], [f1, -k], [-f1, k], [-f1, -k], [f1, k], [-f, -f], [f, f],
    ];
    this.restYZ = yz;
  }
  private restYZ: Array<[number, number]>;
  animate(p: QuadPose): void {
    this.head.rotation.set(p.headX, p.headY, 0);
    const w = p.limbSwing, a = p.limbAmt;
    const ph = [0, PI, PI / 2, PI * 1.5];
    const f3 = ph.map((q) => -(Math.cos(w * 0.6662 * 2 + q) * 0.4) * a);
    const f7 = ph.map((q) => Math.abs(Math.sin(w * 0.6662 + q) * 0.4) * a);
    for (let i = 0; i < 8; i++) {
      const sgn = i % 2 === 0 ? 1 : -1, g = i >> 1;
      this.legs[i].rotation.y = this.restYZ[i][0] + sgn * f3[g];
      this.legs[i].rotation.z = this.restYZ[i][1] + sgn * f7[g];
    }
  }
}

export class PigModel extends Base {
  private head: THREE.Group; private legs: THREE.Group[];
  constructor(env: EnvUniforms) {
    super(env, 'entity/pig/pig_temperate', 64, 64);
    this.head = this.add({ cubes: [cube([0, 0], [-4, -4, -8], [8, 8, 8]), cube([16, 16], [-2, 0, -9], [4, 3, 1])], pos: [0, 12, -6] });
    this.add({ cubes: [cube([28, 8], [-5, -10, -7], [10, 16, 8])], pos: [0, 11, 2], rot: [PI / 2, 0, 0] });
    const leg = (x: number, z: number): THREE.Group => this.add({ cubes: [cube([0, 16], [-2, 0, -2], [4, 6, 4])], pos: [x, 18, z] });
    this.legs = [leg(-3, 7), leg(3, 7), leg(-3, -5), leg(3, -5)];
    // saddle layer (equipment/pig_saddle): head + body, inflated half a pixel
    const sm = this.mat('entity/equipment/pig_saddle/saddle');
    this.saddle.push(this.add({ cubes: [cube([0, 0], [-4, -4, -8], [8, 8, 8], 0.5)] }, sm, this.head));
    this.saddle.push(this.add({ cubes: [cube([28, 8], [-5, -10, -7], [10, 16, 8], 0.5)], pos: [0, 11, 2], rot: [PI / 2, 0, 0] }, sm));
    for (const g of this.saddle) g.visible = false;
  }
  private saddle: THREE.Group[] = [];
  setView(v: MobView): void { for (const g of this.saddle) g.visible = !!v.saddled; }
  animate(p: QuadPose): void { this.head.rotation.set(p.headX, p.headY, 0); quadLegs(this.legs, p); }
}

export class CowModel extends Base {
  private head: THREE.Group; private legs: THREE.Group[];
  constructor(env: EnvUniforms) {
    super(env, 'entity/cow/cow_temperate', 64, 64);
    this.head = this.add({
      cubes: [cube([0, 0], [-4, -4, -6], [8, 8, 6]), cube([22, 0], [-5, -5, -5], [1, 3, 1]), cube([22, 0], [4, -5, -5], [1, 3, 1])],
      pos: [0, 4, -8],
    });
    this.add({ cubes: [cube([18, 4], [-6, -10, -7], [12, 18, 10]), cube([52, 0], [-2, 2, -8], [4, 6, 1])], pos: [0, 5, 2], rot: [PI / 2, 0, 0] });
    const leg = (x: number, z: number): THREE.Group => this.add({ cubes: [cube([0, 16], [-2, 0, -2], [4, 12, 4])], pos: [x, 12, z] });
    this.legs = [leg(-4, 7), leg(4, 7), leg(-4, -6), leg(4, -6)];
  }
  animate(p: QuadPose): void { this.head.rotation.set(p.headX, p.headY, 0); quadLegs(this.legs, p); }
}

export class SheepModel extends Base {
  private head: THREE.Group; private legs: THREE.Group[];
  private woolGroups: THREE.Group[] = [];
  private woolMat: EntityMat;
  constructor(env: EnvUniforms) {
    super(env, 'entity/sheep/sheep');
    this.head = this.add({ cubes: [cube([0, 0], [-3, -4, -6], [6, 6, 8])], pos: [0, 6, -8] });
    this.add({ cubes: [cube([28, 8], [-4, -10, -7], [8, 16, 6])], pos: [0, 5, 2], rot: [PI / 2, 0, 0] });
    const leg = (x: number, z: number): THREE.Group => this.add({ cubes: [cube([0, 16], [-2, 0, -2], [4, 12, 4])], pos: [x, 12, z] });
    this.legs = [leg(-3, 7), leg(3, 7), leg(-3, -5), leg(3, -5)];
    this.woolMat = this.mat('entity/sheep/sheep_wool');
    // wool pieces ride their base part (same pivots), so they animate with it
    this.woolGroups.push(this.add({ cubes: [cube([0, 0], [-3, -4, -4], [6, 6, 6], 0.6)] }, this.woolMat, this.head));
    this.woolGroups.push(this.add({ cubes: [cube([28, 8], [-4, -10, -7], [8, 16, 6], 1.75)], pos: [0, 5, 2], rot: [PI / 2, 0, 0] }, this.woolMat));
    for (const l of this.legs) this.woolGroups.push(this.add({ cubes: [cube([0, 16], [-2, 0, -2], [4, 6, 4], 0.5)] }, this.woolMat, l));
  }
  /** vanilla DyeColor texture colours */
  static readonly COLORS = [
    0xf9fffe, 0xf9801d, 0xc74ebd, 0x3ab3da, 0xfed83d, 0x80c71f, 0xf38baa, 0x474f52,
    0x9d9d97, 0x169c9c, 0x8932b8, 0x3c44aa, 0x835432, 0x5e7c16, 0xb02e26, 0x1d1d21,
  ];
  animate(p: QuadPose): void {
    // SheepModel.prepareMobModel: grazing lowers the head 9 px and nods it
    const t = p.eat ?? 0;
    const pos = t <= 0 ? 0 : t >= 4 && t <= 36 ? 1 : t < 4 ? t / 4 : -(t - 40) / 4;
    this.head.position.y = 6 + pos * 9;
    let hx = p.headX;
    if (t > 4 && t <= 36) hx = PI / 5 + 0.21991149 * Math.sin(((t - 4) / 32) * 28.7);
    else if (t > 0) hx = PI / 5;
    this.head.rotation.set(hx, p.headY, 0);
    quadLegs(this.legs, p);
    this.woolMat.uniforms.uTint.value.setHex(SheepModel.COLORS[p.color & 15] ?? 0xffffff);
    for (const g of this.woolGroups) g.visible = !p.sheared;
  }
}

export class ChickenModel extends Base {
  private head: THREE.Group; private legs: THREE.Group[]; private wings: THREE.Group[];
  constructor(env: EnvUniforms) {
    super(env, 'entity/chicken/chicken_temperate');
    this.head = this.add({
      cubes: [cube([0, 0], [-2, -6, -2], [4, 6, 3]), cube([14, 0], [-2, -4, -4], [4, 2, 2]), cube([14, 4], [-1, -2, -3], [2, 2, 2])],
      pos: [0, 15, -4],
    });
    this.add({ cubes: [cube([0, 9], [-3, -4, -3], [6, 8, 6])], pos: [0, 16, 0], rot: [PI / 2, 0, 0] });
    this.legs = [
      this.add({ cubes: [cube([26, 0], [-1, 0, -3], [3, 5, 3])], pos: [-2, 19, 1] }),
      this.add({ cubes: [cube([26, 0], [-1, 0, -3], [3, 5, 3])], pos: [1, 19, 1] }),
    ];
    this.wings = [
      this.add({ cubes: [cube([24, 13], [0, 0, -3], [1, 4, 6])], pos: [-4, 13, 0] }),
      this.add({ cubes: [cube([24, 13], [-1, 0, -3], [1, 4, 6])], pos: [4, 13, 0] }),
    ];
  }
  animate(p: QuadPose): void {
    this.head.rotation.set(p.headX, p.headY, 0);
    const s = p.limbSwing * 0.6662, a = p.limbAmt * 1.4;
    this.legs[0].rotation.x = Math.cos(s) * a;
    this.legs[1].rotation.x = Math.cos(s + PI) * a;
    const flap = p.airborne ? (Math.sin(p.age * 1.1) + 1) * 0.9 : 0;
    this.wings[0].rotation.z = flap;
    this.wings[1].rotation.z = -flap;
  }
}
